import type { EffortLevel } from '@/api/v2/settings'
import type { EffortSource } from '@/models/Task'

/**
 * "Meine Woche" resources as returned by /api/v2/my-week.
 * Mirrors App\Serializers\V2\MyWeek\* in the backend (MyWeek, MyWeekEntry, MyWeekPlan,
 * MyWeekSuggestion, MyWeekLookup). Timestamps are ms; dates are "YYYY-MM-DD" in the user's
 * timezone.
 */

/** Live state of an entry (App\Services\MyWeek\EntryState). */
export type MyWeekEntryState = 'planned' | 'waiting' | 'done'

/** The one chip of an entry, first match wins (App\Services\MyWeek\EntryState::signal). */
export type MyWeekSignal = 'overdue' | 'customer_replied' | 'decision_needed' | 'due'

/** Why a task is suggested (App\UseCases\Internal\MyWeek\SuggestionCandidates). */
export type MyWeekSuggestionReason = 'overdue' | 'customer_replied' | 'due_soon' | 'recently_assigned'

/** Week targets the entry endpoints accept. */
export type MyWeekTarget = 'current' | 'next'

/** Carries after which taking an entry into the current week needs a decision. */
export const DECISION_AFTER_CARRIES = 3

/** The soft limit of the "Heute" block. */
export const TODAY_SOFT_LIMIT = 3

export interface MyWeekTaskEffort {
  /** Effective level key; null when an estimation wins. */
  level: string | null
  minutes: number
  source: EffortSource
}

export interface MyWeekTaskAssignee {
  user_id: string | null
  status: string | null
}

/** The task as the week list shows it (MyWeekEntry::taskSummary). */
export interface MyWeekTaskSummary {
  id: string
  prefix: string | null
  number: number | null
  title: string | null
  contact_names: string[]
  /** ms timestamp */
  due_date: number | null
  /** The due date as a calendar day in the user's timezone — all day comparisons use this. */
  due_date_local: string | null
  overall_status: string
  email_status: 'waiting_for_employee' | 'waiting_for_customer' | null
  assignees: MyWeekTaskAssignee[]
  effort: MyWeekTaskEffort
}

export interface MyWeekEntry {
  id: string
  task_id: string
  /** ISO week, e.g. "2026-W42" */
  week: string
  position: number
  /** The date the entry was put on "Heute"; a stale date means it is not on "Heute" any more. */
  today_date: string | null
  /** On "Heute" right now — computed by the backend from today_date, the user's today and state. */
  today: boolean
  state: MyWeekEntryState
  signal: MyWeekSignal | null
  /** ms timestamp of when the entry became done (own assignee status closed); null when open */
  done_at: number | null
  /** Done on the user's today — computed by the backend. */
  done_today: boolean
  /** The day it became done, "YYYY-MM-DD" in the user's timezone; null when open */
  done_date: string | null
  carry_count: number
  /**
   * ms timestamp of the "Bleibt diese Woche" decision. Always null for entries of past weeks: an
   * old decision does not carry over.
   */
  decided_at: number | null
  task: MyWeekTaskSummary
  created_at: number
  updated_at: number
}

export interface MyWeekInfo {
  iso: string
  /** Monday, "YYYY-MM-DD" */
  start: string
  /** Sunday, "YYYY-MM-DD" */
  end: string
  /** The user's today, "YYYY-MM-DD" — the client never derives "today" from its own clock. */
  today: string
  /** ISO weekday of the user's today, 1 = Monday … 7 = Sunday */
  weekday: number
  is_current: boolean
}

export interface MyWeekPlan {
  week: string
  /** "YYYY-MM-DD" dates inside the week */
  days_off: string[]
  appointment_minutes: number
  started: boolean
  planned_at: number | null
  updated_at: number | null
}

/** All values in minutes except the counters (App\Services\MyWeek\CapacityCalculator). */
export interface MyWeekCapacity {
  capacity: number
  buffer: number
  plannable: number
  planned: number
  done: number
  free: number
  workdays: number
  hours_per_day: number
  buffer_percent: number
}

export interface MyWeek {
  week: MyWeekInfo
  plan: MyWeekPlan
  capacity: MyWeekCapacity
  effort_levels: EffortLevel[]
  default_effort_level: string
  entries: MyWeekEntry[]
  /** Always relative to the current week. */
  next_week: MyWeekEntry[]
  /** Own entries of older weeks that are not done — always relative to the current week. */
  carry_over: MyWeekEntry[]
  done_count: number
  total_count: number
}

export interface MyWeekSuggestion {
  reason: MyWeekSuggestionReason | null
  rank: number
  task: MyWeekTaskSummary
}

export interface MyWeekLookup {
  task_id: string
  entry_id: string | null
  week: string | null
}

/** Machine codes the my-week endpoints put into the error `details`. */
export const MyWeekErrors = {
  DECISION_REQUIRED: 'DECISION_REQUIRED',
  NOT_IN_CURRENT_WEEK: 'NOT_IN_CURRENT_WEEK',
  INVALID_HANDOVER_TARGET: 'INVALID_HANDOVER_TARGET',
  EDIT_PERMISSION_REQUIRED: 'EDIT_PERMISSION_REQUIRED',
  NOT_ASSIGNEE: 'NOT_ASSIGNEE',
} as const

/** "TB-1042"; falls back to the title when the task has no number yet. */
export function taskKey(task: Pick<MyWeekTaskSummary, 'prefix' | 'number' | 'title'>): string {
  if (task.prefix && task.number) return `${task.prefix}-${task.number}`
  if (task.number) return String(task.number)
  return task.title ?? ''
}
